/**
 * NotificationsPage Component
 *
 * Displays user notifications with mark as read functionality.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from './components';
import { useNotifications, NotificationType } from './hooks';

export const NotificationsPage: React.FC = () => {
  const { notifications, isLoading, markAsRead, markAllAsRead, clearAll } = useNotifications();

  const getNotificationLink = (notification: { type: NotificationType; data?: { bookingId?: string; threadId?: string; listingId?: string; actionUrl?: string } }): string => {
    switch (notification.type) {
      case 'booking_confirmed':
        if (notification.data?.actionUrl) {
          return notification.data.actionUrl;
        }
        return notification.data?.bookingId
          ? `/dashboard/bookings/${notification.data.bookingId}`
          : '/dashboard/bookings';
      case 'booking_cancelled':
      case 'booking_request':
      case 'payment':
        return notification.data?.bookingId
          ? `/dashboard/bookings/${notification.data.bookingId}`
          : '/dashboard/bookings';
      case 'message':
        return notification.data?.threadId
          ? `/dashboard/messages/${notification.data.threadId}`
          : '/dashboard/messages';
      case 'review':
        return notification.data?.bookingId
          ? `/dashboard/bookings/${notification.data.bookingId}`
          : '/dashboard/bookings?tab=past';
      case 'reminder':
        return notification.data?.bookingId
          ? `/dashboard/bookings/${notification.data.bookingId}`
          : '/dashboard/bookings';
      default:
        return '/dashboard';
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <DashboardLayout title="Notifications">
      {/* Actions */}
      {notifications.length > 0 && (
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500">
            {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
          </p>
          <div className="flex gap-3">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Mark all as read
              </button>
            )}
            <button
              onClick={() => {
                if (confirm('Clear all notifications?')) {
                  clearAll();
                }
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* Notifications List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse flex gap-4 p-4 bg-gray-100 rounded-xl">
              <div className="w-10 h-10 bg-gray-200 rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : notifications.length > 0 ? (
        <div className="space-y-2">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              link={getNotificationLink(notification)}
              onMarkAsRead={() => markAsRead(notification.id)}
            />
          ))}
        </div>
      ) : (
        <EmptyState />
      )}
    </DashboardLayout>
  );
};

// Notification Item
interface NotificationItemProps {
  notification: {
    id: string;
    type: NotificationType;
    title: string;
    message: string;
    read: boolean;
    createdAt: Date;
  };
  link: string;
  onMarkAsRead: () => void;
}

const formatTime = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (minutes < 60) {
    return minutes <= 1 ? 'Just now' : `${minutes}m ago`;
  } else if (hours < 24) {
    return `${hours}h ago`;
  } else if (days === 1) {
    return 'Yesterday';
  } else if (days < 7) {
    return `${days}d ago`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

// Icons (must be defined before notificationIcons)
const CheckCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const XCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const MessageIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

const CreditCardIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const BellIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const InfoIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const notificationIcons: Record<NotificationType, React.FC<{ className?: string }>> = {
  booking_confirmed: CheckCircleIcon,
  booking_cancelled: XCircleIcon,
  booking_request: CalendarIcon,
  message: MessageIcon,
  review: StarIcon,
  payment: CreditCardIcon,
  reminder: BellIcon,
  system: InfoIcon,
};

const notificationColors: Record<NotificationType, string> = {
  booking_confirmed: 'bg-green-100 text-green-600',
  booking_cancelled: 'bg-red-100 text-red-600',
  booking_request: 'bg-blue-100 text-blue-600',
  message: 'bg-primary-100 text-primary-600',
  review: 'bg-amber-100 text-amber-600',
  payment: 'bg-purple-100 text-purple-600',
  reminder: 'bg-orange-100 text-orange-600',
  system: 'bg-gray-100 text-gray-600',
};

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  link,
  onMarkAsRead,
}) => {
  const Icon = notificationIcons[notification.type] || BellIcon;
  const iconColor = notificationColors[notification.type] || 'bg-gray-100 text-gray-600';

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead();
    }
  };

  return (
    <Link
      to={link}
      onClick={handleClick}
      className={`flex items-start gap-4 p-4 rounded-xl transition-colors ${
        notification.read
          ? 'bg-white hover:bg-gray-50 border border-gray-200'
          : 'bg-primary-50 hover:bg-primary-100 border border-primary-100'
      }`}
    >
      {/* Icon */}
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconColor}`}>
        <Icon className="w-5 h-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h3 className={`font-medium ${notification.read ? 'text-gray-700' : 'text-gray-900'}`}>
            {notification.title}
          </h3>
          <span className="text-xs text-gray-500 flex-shrink-0">
            {formatTime(notification.createdAt)}
          </span>
        </div>
        <p className={`text-sm ${notification.read ? 'text-gray-500' : 'text-gray-700'}`}>
          {notification.message}
        </p>
      </div>

      {/* Unread indicator */}
      {!notification.read && (
        <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0 mt-2" />
      )}
    </Link>
  );
};

// Empty State
const EmptyState: React.FC = () => (
  <div className="text-center py-16 px-4">
    <div className="text-gray-300 mb-4 flex justify-center">
      <BellIcon className="w-16 h-16" />
    </div>
    <h3 className="text-xl font-medium text-gray-900 mb-2">No notifications</h3>
    <p className="text-gray-600 max-w-sm mx-auto">
      When you have updates about your bookings or messages, they'll appear here.
    </p>
  </div>
);

export default NotificationsPage;
